/**
 * NOVA-037 — the per-discipline TAT view. The server fans out tat_signoff into one row per discipline
 * (discipline_code populated, AN-2 aggregate). This table lists those per-discipline rows (the lab-wide
 * null row stays on the KPI tile), each with its average sign-off TAT + the sample count + the rapport
 * tone band (color + glyph + text — never color alone). Hidden when there is no per-discipline TAT yet.
 */
import { Card, Table, type Column } from "@/ui";
import { useT } from "@/app";
import { formatMetric, metaFor, toneFor, TONE_COLOR, TONE_GLYPH } from "../lib";
import type { LiveMetric } from "../types";

export function DisciplineTatTable({ rows }: { rows: ReadonlyArray<LiveMetric> }) {
  const { t } = useT();
  const meta = metaFor("tat_signoff");

  const columns: ReadonlyArray<Column<LiveMetric>> = [
    {
      key: "discipline",
      header: t("analytics.discipline.col.code"),
      mono: true,
      cell: (r) => <span className="nv-ltr-island">{r.discipline_code}</span>,
    },
    {
      key: "tat",
      header: t("analytics.discipline.col.tat"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.metric_value,
      cell: (r) => <span className="nv-ltr-island">{formatMetric(r.metric_value, meta.format)}</span>,
    },
    {
      key: "count",
      header: t("analytics.discipline.col.count"),
      mono: true,
      align: "end",
      sortable: true,
      sortAccessor: (r) => r.count,
      cell: (r) => <span className="nv-ltr-island">{r.count}</span>,
    },
    {
      key: "tone",
      header: t("analytics.discipline.col.tone"),
      cell: (r) => {
        const tone = toneFor(meta, r.metric_value);
        return (
          <span className="nv-badge" style={{ color: TONE_COLOR[tone], background: "var(--nv-surface-2)" }}>
            <span className="nv-badge__glyph" aria-hidden>
              {TONE_GLYPH[tone]}
            </span>
            {t(`analytics.tone.${tone}`)}
          </span>
        );
      },
    },
  ];

  return (
    <Card title={t("analytics.discipline.title")} padded={false}>
      <div style={{ padding: "var(--nv-s-3)", display: "flex", flexDirection: "column", gap: "var(--nv-s-3)" }}>
        <p style={{ margin: 0, color: "var(--nv-slate)", fontSize: "var(--nv-fs-caption)" }}>
          {t("analytics.discipline.subtitle")}
        </p>
        <Table
          columns={columns}
          rows={[...rows]}
          rowKey={(r) => String(r.discipline_code)}
          caption={t("analytics.discipline.title")}
          emptyTitle={t("analytics.discipline.empty")}
          pageSize={10}
        />
      </div>
    </Card>
  );
}
